/**
 * Dashboard Feature - Redux Slice
 * UI state management for dashboard
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Map view state
 */
export interface MapViewState {
  zoom: number;
  center: [number, number]; // [lng, lat]
  bearing: number;
  pitch: number;
}

/**
 * Active overlay types (map overlays)
 */
export type OverlayType =
  | "none"
  | "weather"
  | "satellite"
  | "ndvi"
  | "lga"
  | "operations";

/**
 * Modal overlay types (import/add overlays)
 */
export type ModalOverlayType =
  | "none"
  | "import-interface" // Main import options modal
  | "geometry-import" // Shapefile upload for field boundaries
  | "operations-import" // Operations data import
  | "plan-import" // Growing season plan import
  | "financial-import" // Financial statements import
  | "harvest-import" // Historical harvest data import
  | "add-client" // Broker: Add new client
  | "field-info" // Field details panel
  | "weather-info"; // Weather station details

/**
 * Info panel section (Plan, Operations, Insurance) for sidebar-triggered open
 */
export type InfoPanelSection = "plan" | "operations" | "insurance";

/**
 * Sidebar expansion state
 */
export interface SidebarExpansion {
  farmId: number;
  isExpanded: boolean;
}

/**
 * Client centroid for broker view
 */
export interface ClientCentroid {
  clientOrgId: number;
  clientOrgName: string;
  accessLevel: number;
  coordinates: { lat: number; lng: number };
}

/**
 * Dashboard UI state shape
 */
export interface DashboardState {
  // Sidebar
  isSidebarOpen: boolean;
  expandedFarmIds: number[];
  selectedFarmId: number | null;
  selectedFieldId: number | null;

  // Multi-field selection (like Old Project)
  selectedFieldIds: number[];

  // Search term for filtering farms/fields
  searchTerm: string;

  // Map
  mapView: MapViewState;
  activeOverlay: OverlayType;
  showLabels: boolean;

  // Overlays panel (map layer toggles)
  isOverlaysPanelOpen: boolean;

  // Modal overlays (import/add dialogs)
  activeModalOverlay: ModalOverlayType;

  // Selected broker client (for broker view)
  selectedClientOrgId: number | null;

  // Broker client centroids (for map markers)
  clientsCentroids: ClientCentroid[];

  // Selected weather station (for weather info overlay)
  selectedWeatherStationId: number | null;
  // Selected farm (for weather info overlay when opened from farm centroid click)
  selectedFarmIdForWeather: number | null;

  // Operations filters
  selectedYear: number | null;
  selectedSeason: string | null;

  // Right-side info panel: section to expand when opened from Field Details (e.g. Plan, Operations, Insurance)
  infoPanelOpenSection: InfoPanelSection | null;

  // Loading states managed by React Query, but we track some UI states
  isMapReady: boolean;
}

/**
 * Default map view centered on Australia
 */
const defaultMapView: MapViewState = {
  zoom: 4,
  center: [133.7751, -25.2744], // Australia center
  bearing: 0,
  pitch: 0,
};

/**
 * Initial dashboard state
 */
const initialState: DashboardState = {
  // Sidebar
  isSidebarOpen: false,
  expandedFarmIds: [],
  selectedFarmId: null,
  selectedFieldId: null,

  // Multi-field selection
  selectedFieldIds: [],

  // Search
  searchTerm: "",

  // Map
  mapView: defaultMapView,
  activeOverlay: "none",
  showLabels: true,

  // Overlays panel (map layers)
  isOverlaysPanelOpen: false,

  // Modal overlays (import/add dialogs)
  activeModalOverlay: "none",

  // Broker
  selectedClientOrgId: null,
  clientsCentroids: [],

  // Weather
  selectedWeatherStationId: null,
  selectedFarmIdForWeather: null,

  // Operations filters
  selectedYear: null,
  selectedSeason: null,

  // Right-side info panel
  infoPanelOpenSection: null,

  // UI flags
  isMapReady: false,
};

/**
 * Dashboard slice with reducers
 */
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // ========================================================================
    // SIDEBAR
    // ========================================================================
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    // Farm expansion
    toggleFarmExpansion: (state, action: PayloadAction<number>) => {
      const farmId = action.payload;
      const index = state.expandedFarmIds.indexOf(farmId);
      if (index === -1) {
        state.expandedFarmIds.push(farmId);
      } else {
        state.expandedFarmIds.splice(index, 1);
      }
    },
    setFarmExpanded: (
      state,
      action: PayloadAction<{ farmId: number; isExpanded: boolean }>
    ) => {
      const { farmId, isExpanded } = action.payload;
      const index = state.expandedFarmIds.indexOf(farmId);
      if (isExpanded && index === -1) {
        state.expandedFarmIds.push(farmId);
      } else if (!isExpanded && index !== -1) {
        state.expandedFarmIds.splice(index, 1);
      }
    },
    expandAllFarms: (state, action: PayloadAction<number[]>) => {
      state.expandedFarmIds = action.payload;
    },
    collapseAllFarms: (state) => {
      state.expandedFarmIds = [];
    },

    // Selection
    selectFarm: (state, action: PayloadAction<number | null>) => {
      state.selectedFarmId = action.payload;
      // Clear field selection when selecting a farm
      if (action.payload !== null) {
        state.selectedFieldId = null;
      }
    },
    selectField: (
      state,
      action: PayloadAction<{ farmId: number; fieldId: number } | null>
    ) => {
      if (action.payload) {
        state.selectedFarmId = action.payload.farmId;
        state.selectedFieldId = action.payload.fieldId;
        // Ensure the farm is expanded
        if (!state.expandedFarmIds.includes(action.payload.farmId)) {
          state.expandedFarmIds.push(action.payload.farmId);
        }
      } else {
        state.selectedFieldId = null;
      }
    },
    clearSelection: (state) => {
      state.selectedFarmId = null;
      state.selectedFieldId = null;
    },

    // ========================================================================
    // MULTI-FIELD SELECTION
    // ========================================================================
    /**
     * Add a field ID to the selection
     */
    addFieldIdToSelected: (state, action: PayloadAction<number>) => {
      const fieldId = action.payload;
      if (!state.selectedFieldIds.includes(fieldId)) {
        state.selectedFieldIds.push(fieldId);
      }
    },

    /**
     * Remove a field ID from the selection
     */
    removeFieldIdFromSelected: (state, action: PayloadAction<number>) => {
      const fieldId = action.payload;
      state.selectedFieldIds = state.selectedFieldIds.filter(
        (id) => id !== fieldId
      );
    },

    /**
     * Toggle a field's selection state
     */
    toggleFieldSelection: (state, action: PayloadAction<number>) => {
      const fieldId = action.payload;
      const index = state.selectedFieldIds.indexOf(fieldId);
      if (index === -1) {
        state.selectedFieldIds.push(fieldId);
      } else {
        state.selectedFieldIds.splice(index, 1);
      }
    },

    /**
     * Select all fields from a specific farm
     */
    selectAllFarmFields: (
      state,
      action: PayloadAction<{ farmId: number; fieldIds: number[] }>
    ) => {
      const { fieldIds } = action.payload;
      fieldIds.forEach((fieldId) => {
        if (!state.selectedFieldIds.includes(fieldId)) {
          state.selectedFieldIds.push(fieldId);
        }
      });
    },

    /**
     * Unselect all fields from a specific farm
     */
    unselectAllFarmFields: (
      state,
      action: PayloadAction<{ farmId: number; fieldIds: number[] }>
    ) => {
      const { fieldIds } = action.payload;
      state.selectedFieldIds = state.selectedFieldIds.filter(
        (id) => !fieldIds.includes(id)
      );
    },

    /**
     * Toggle all fields from a specific farm
     */
    toggleAllFarmFields: (
      state,
      action: PayloadAction<{ farmId: number; fieldIds: number[] }>
    ) => {
      const { fieldIds } = action.payload;
      const allSelected = fieldIds.every((id) =>
        state.selectedFieldIds.includes(id)
      );
      if (allSelected) {
        // Unselect all farm fields
        state.selectedFieldIds = state.selectedFieldIds.filter(
          (id) => !fieldIds.includes(id)
        );
      } else {
        // Select all farm fields
        fieldIds.forEach((fieldId) => {
          if (!state.selectedFieldIds.includes(fieldId)) {
            state.selectedFieldIds.push(fieldId);
          }
        });
      }
    },

    /**
     * Select all fields (across all farms)
     */
    selectAllFields: (state, action: PayloadAction<number[]>) => {
      state.selectedFieldIds = action.payload;
    },

    /**
     * Unselect all fields
     */
    unselectAllFields: (state) => {
      state.selectedFieldIds = [];
    },

    /**
     * Clear all field selections
     */
    clearFieldSelections: (state) => {
      state.selectedFieldIds = [];
    },

    // ========================================================================
    // SEARCH
    // ========================================================================
    /**
     * Set the search term for filtering farms/fields
     */
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },

    /**
     * Clear the search term
     */
    clearSearchTerm: (state) => {
      state.searchTerm = "";
    },

    // ========================================================================
    // MAP
    // ========================================================================
    setMapView: (state, action: PayloadAction<Partial<MapViewState>>) => {
      state.mapView = { ...state.mapView, ...action.payload };
    },
    resetMapView: (state) => {
      state.mapView = defaultMapView;
    },
    setMapReady: (state, action: PayloadAction<boolean>) => {
      state.isMapReady = action.payload;
    },

    // Zoom to specific coordinates
    flyTo: (
      state,
      action: PayloadAction<{
        center: [number, number];
        zoom?: number;
      }>
    ) => {
      state.mapView.center = action.payload.center;
      if (action.payload.zoom !== undefined) {
        state.mapView.zoom = action.payload.zoom;
      }
    },

    // ========================================================================
    // OVERLAYS
    // ========================================================================
    setActiveOverlay: (state, action: PayloadAction<OverlayType>) => {
      state.activeOverlay = action.payload;
    },
    toggleLabels: (state) => {
      state.showLabels = !state.showLabels;
    },
    setShowLabels: (state, action: PayloadAction<boolean>) => {
      state.showLabels = action.payload;
    },
    toggleOverlaysPanel: (state) => {
      state.isOverlaysPanelOpen = !state.isOverlaysPanelOpen;
    },
    setOverlaysPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isOverlaysPanelOpen = action.payload;
    },

    // ========================================================================
    // MODAL OVERLAYS (Import/Add dialogs)
    // ========================================================================
    openModalOverlay: (state, action: PayloadAction<ModalOverlayType>) => {
      state.activeModalOverlay = action.payload;
    },
    closeModalOverlay: (state) => {
      state.activeModalOverlay = "none";
      state.selectedWeatherStationId = null;
      state.selectedFarmIdForWeather = null;
    },
    openImportInterface: (state) => {
      state.activeModalOverlay = "import-interface";
    },
    openGeometryImport: (state) => {
      state.activeModalOverlay = "geometry-import";
    },
    openOperationsImport: (state) => {
      state.activeModalOverlay = "operations-import";
    },
    openPlanImport: (state) => {
      state.activeModalOverlay = "plan-import";
    },
    openFinancialImport: (state) => {
      state.activeModalOverlay = "financial-import";
    },
    openHarvestImport: (state) => {
      state.activeModalOverlay = "harvest-import";
    },
    openAddClient: (state) => {
      state.activeModalOverlay = "add-client";
    },
    openFieldInfo: (
      state,
      action: PayloadAction<{ farmId: number; fieldId: number }>
    ) => {
      state.activeModalOverlay = "field-info";
      state.selectedFarmId = action.payload.farmId;
      state.selectedFieldId = action.payload.fieldId;
    },
    openWeatherInfo: (state, action: PayloadAction<number>) => {
      state.activeModalOverlay = "weather-info";
      state.selectedWeatherStationId = action.payload;
      state.selectedFarmIdForWeather = null;
    },
    openWeatherInfoByFarmId: (state, action: PayloadAction<number>) => {
      state.activeModalOverlay = "weather-info";
      state.selectedFarmIdForWeather = action.payload;
      state.selectedWeatherStationId = null;
    },

    // ========================================================================
    // BROKER VIEW
    // ========================================================================
    selectClient: (state, action: PayloadAction<number | null>) => {
      state.selectedClientOrgId = action.payload;
      // Reset farm/field selection when switching clients
      state.selectedFarmId = null;
      state.selectedFieldId = null;
      state.expandedFarmIds = [];
    },
    setClientsCentroids: (state, action: PayloadAction<ClientCentroid[]>) => {
      state.clientsCentroids = action.payload;
    },
    clearClientsCentroids: (state) => {
      state.clientsCentroids = [];
    },

    // ========================================================================
    // OPERATIONS FILTERS
    // ========================================================================
    setSelectedYear: (state, action: PayloadAction<number | null>) => {
      state.selectedYear = action.payload;
    },
    setSelectedSeason: (state, action: PayloadAction<string | null>) => {
      state.selectedSeason = action.payload;
    },
    clearOperationsFilters: (state) => {
      state.selectedYear = null;
      state.selectedSeason = null;
    },

    /**
     * Open the right-side info panel with a specific section expanded (from Field Details sidebar).
     */
    openInfoPanelSection: (
      state,
      action: PayloadAction<InfoPanelSection | null>
    ) => {
      state.infoPanelOpenSection = action.payload;
    },
    clearInfoPanelOpenSection: (state) => {
      state.infoPanelOpenSection = null;
    },

    // ========================================================================
    // RESET
    // ========================================================================
    resetDashboardState: () => initialState,
  },
});

// Export actions
export const {
  // Sidebar
  toggleSidebar,
  setSidebarOpen,
  toggleFarmExpansion,
  setFarmExpanded,
  expandAllFarms,
  collapseAllFarms,
  selectFarm,
  selectField,
  clearSelection,
  // Multi-field selection
  addFieldIdToSelected,
  removeFieldIdFromSelected,
  toggleFieldSelection,
  selectAllFarmFields,
  unselectAllFarmFields,
  toggleAllFarmFields,
  selectAllFields,
  unselectAllFields,
  clearFieldSelections,
  // Search
  setSearchTerm,
  clearSearchTerm,
  // Map
  setMapView,
  resetMapView,
  setMapReady,
  flyTo,
  // Map overlays
  setActiveOverlay,
  toggleLabels,
  setShowLabels,
  toggleOverlaysPanel,
  setOverlaysPanelOpen,
  // Modal overlays
  openModalOverlay,
  closeModalOverlay,
  openImportInterface,
  openGeometryImport,
  openOperationsImport,
  openPlanImport,
  openFinancialImport,
  openHarvestImport,
  openAddClient,
  openFieldInfo,
  openWeatherInfo,
  openWeatherInfoByFarmId,
  openInfoPanelSection,
  clearInfoPanelOpenSection,
  // Broker
  selectClient,
  setClientsCentroids,
  clearClientsCentroids,
  // Operations filters
  setSelectedYear,
  setSelectedSeason,
  clearOperationsFilters,
  // Reset
  resetDashboardState,
} = dashboardSlice.actions;

// Export reducer
export default dashboardSlice.reducer;

// Export selectors
export const selectIsSidebarOpen = (state: { dashboard: DashboardState }) =>
  state.dashboard.isSidebarOpen;

export const selectExpandedFarmIds = (state: { dashboard: DashboardState }) =>
  state.dashboard.expandedFarmIds;

export const selectSelectedFarmId = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedFarmId;

export const selectSelectedFieldId = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedFieldId;

export const selectMapView = (state: { dashboard: DashboardState }) =>
  state.dashboard.mapView;

export const selectActiveOverlay = (state: { dashboard: DashboardState }) =>
  state.dashboard.activeOverlay;

export const selectShowLabels = (state: { dashboard: DashboardState }) =>
  state.dashboard.showLabels;

export const selectIsOverlaysPanelOpen = (state: {
  dashboard: DashboardState;
}) => state.dashboard.isOverlaysPanelOpen;

export const selectSelectedClientOrgId = (state: {
  dashboard: DashboardState;
}) => state.dashboard.selectedClientOrgId;

export const selectClientsCentroids = (state: { dashboard: DashboardState }) =>
  state.dashboard.clientsCentroids;

export const selectIsMapReady = (state: { dashboard: DashboardState }) =>
  state.dashboard.isMapReady;

export const selectActiveModalOverlay = (state: {
  dashboard: DashboardState;
}) => state.dashboard.activeModalOverlay;

export const selectSelectedWeatherStationId = (state: {
  dashboard: DashboardState;
}) => state.dashboard.selectedWeatherStationId;

export const selectSelectedFarmIdForWeather = (state: {
  dashboard: DashboardState;
}) => state.dashboard.selectedFarmIdForWeather;

export const selectIsImportInterfaceOpen = (state: {
  dashboard: DashboardState;
}) => state.dashboard.activeModalOverlay === "import-interface";

export const selectSelectedYear = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedYear;

export const selectSelectedSeason = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedSeason;

export const selectInfoPanelOpenSection = (state: {
  dashboard: DashboardState;
}) => state.dashboard.infoPanelOpenSection;

// Multi-field selection selectors
export const selectSelectedFieldIds = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedFieldIds;

export const selectIsFieldSelected = (
  state: { dashboard: DashboardState },
  fieldId: number
) => state.dashboard.selectedFieldIds.includes(fieldId);

export const selectSelectedFieldsCount = (state: {
  dashboard: DashboardState;
}) => state.dashboard.selectedFieldIds.length;

export const selectHasSelectedFields = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedFieldIds.length > 0;

// Search selectors
export const selectSearchTerm = (state: { dashboard: DashboardState }) =>
  state.dashboard.searchTerm;

export const selectHasSearchTerm = (state: { dashboard: DashboardState }) =>
  state.dashboard.searchTerm.length > 0;
